/* eslint-disable */
import { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Button,
} from "@mui/material";
import { Header } from "../../Components/Header";
import { HeroSection } from "../../Components/HeroSection";
import { PropertyCard } from "../../Components/PropertyCard";
import { PropertyListView } from "../../Components/PropertyListView";
import { ViewModeSelector } from "../../Components/ViewModeSelector";
import { Footer } from "../../Components/Footer";
import { Testimonials } from "../../Components/Testimonials";
import {
  ScrollReveal,
  MouseScrollIndicator,
} from "../../Components/ScrollAnimation";
import { useAuth } from "../../Contexts/AuthContext";
import { propertyAPI, savedPropertyAPI } from "../../services/api";
import { transformProperties } from "../../Utils/propertyTransform";
import { encryptRouteParam } from "../../Utils/encrypt-decrypt";
import Sidebar from "../../Layouts/SideBar/sidebar";
import Drawer from "../../Layouts/MainContent/drawer";
import { useDrawer } from "../../Hooks/useDrawer";
import useWindowResize from "../../Hooks/useWindowResize";

// Lazy load heavy components
const PropertyMapView = lazy(() => import("../../Components/PropertyMapView"));
const PropertyListingForm = lazy(() =>
  import("../../Components/PropertyListingForm")
);

export function Landing() {
  const MIN_PRICE = 1000000;
  const MAX_PRICE = 500000000;

  const normalizePriceRange = (range) => {
    if (!Array.isArray(range) || range.length !== 2) {
      return [MIN_PRICE, MAX_PRICE];
    }

    const min = Number(range[0]);
    const max = Number(range[1]);

    const normalizedMin = Number.isFinite(min)
      ? Math.max(MIN_PRICE, Math.min(min, MAX_PRICE))
      : MIN_PRICE;
    const normalizedMax = Number.isFinite(max)
      ? Math.max(MIN_PRICE, Math.min(max, MAX_PRICE))
      : MAX_PRICE;

    if (normalizedMin > normalizedMax) {
      return [MIN_PRICE, MAX_PRICE];
    }

    return [normalizedMin, normalizedMax];
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, loggedIn, logout } = useAuth();
  const isLoggedIn = loggedIn; // Map loggedIn to isLoggedIn for compatibility
  const user = userProfile; // Map userProfile to user for compatibility
  const roleId = userProfile?.role_id ?? userProfile?.roleId;
  const isAdmin = Number(roleId) === 1;
  const { isOpen, toggleDrawer } = useDrawer();
  const isDesktopView = useWindowResize();

  // Persist viewMode in sessionStorage so it survives navigation
  const [viewMode, setViewModeState] = useState(() => {
    return sessionStorage.getItem("propertyViewMode") || "grid";
  });
  // Persist search state in sessionStorage
  const [searchQuery, setSearchQueryState] = useState(() => {
    return sessionStorage.getItem("propertySearchQuery") || "";
  });
  const [isSearchMode, setIsSearchModeState] = useState(() => {
    return sessionStorage.getItem("propertyIsSearchMode") === "true";
  });

  const setSearchQuery = (query) => {
    sessionStorage.setItem("propertySearchQuery", query);
    setSearchQueryState(query);
  };

  const setIsSearchMode = (mode) => {
    sessionStorage.setItem("propertyIsSearchMode", String(mode));
    setIsSearchModeState(mode);
  };

  const getQueryParamValue = (search, key) => {
    const params = new URLSearchParams(search || "");
    return (params.get(key) || "").trim();
  };

  const getStateSearchQuery = () => {
    const stateQuery = location?.state?.seoSearchQuery;
    return stateQuery ? String(stateQuery).trim() : "";
  };

  useEffect(() => {
    if (location.pathname !== "/") return;

    const queryFromUrl = getQueryParamValue(location.search, "q");
    const queryFromState = getStateSearchQuery();
    const effectiveQuery = queryFromState || queryFromUrl;

    if (effectiveQuery) {
      if (effectiveQuery !== searchQuery) {
        setSearchQuery(effectiveQuery);
      }
      if (!isSearchMode) {
        setIsSearchMode(true);
      }

      fetchProperties(true, {
        isSearchMode: true,
        searchQuery: effectiveQuery,
      });

      if (queryFromUrl) {
        navigate("/", {
          replace: true,
          state: {
            ...(location.state || {}),
            seoSearchQuery: effectiveQuery,
          },
        });
      }
      return;
    }

    if (isSearchMode || searchQuery) {
      setSearchQuery("");
      setIsSearchMode(false);
    }

    fetchProperties(true, {
      isSearchMode: false,
      searchQuery: "",
    });
  }, [location.pathname, location.search, location.state]);

  useEffect(() => {
    if (location.pathname !== "/") return;

    const queryFromUrl = getQueryParamValue(location.search, "q");
    const queryFromState = getStateSearchQuery();
    const normalizedQuery = queryFromState || queryFromUrl || searchQuery;
    const isQuerySearch = Boolean(normalizedQuery);

    const defaultTitle = "Vruddhi Properties - The best real estate portal for Mangalore and Udupi";
    const defaultDescription = "Vruddhi Properties - Your go to platform to manage your properties needs";
    const title = isQuerySearch
      ? `${normalizedQuery} | Vruddhi Properties`
      : defaultTitle;
    const description = isQuerySearch
      ? `Search results for ${normalizedQuery} on Vruddhi Properties. Explore listings by locality, BHK type, amenities, and budget in Karnataka.`
      : defaultDescription;

    const canonicalUrl = `${window.location.origin}/`;

    const setMetaByName = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setMetaByProperty = (property, content) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setCanonicalLink = (href) => {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", href);
    };

    document.title = title;
    setMetaByName("description", description);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", canonicalUrl);
    setMetaByProperty("og:type", "website");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setCanonicalLink(canonicalUrl);
  }, [location.pathname, location.search, location.state, searchQuery]);

  // Initialize properties and loading state from cache if available
  const [properties, setProperties] = useState(() => {
    try {
      const cachedData = sessionStorage.getItem("propertiesCache");
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
        if (cacheAge < CACHE_DURATION && data && data.length > 0) {
          return data;
        }
      }
    } catch (e) {}
    return [];
  });

  const [loading, setLoading] = useState(() => {
    try {
      const cachedData = sessionStorage.getItem("propertiesCache");
      if (cachedData) {
        const { timestamp, data } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        const CACHE_DURATION = 5 * 60 * 1000;
        if (cacheAge < CACHE_DURATION && data && data.length > 0) {
          return false;
        }
      }
    } catch (e) {}
    return true;
  });

  const [loadingMore, setLoadingMore] = useState(false);
  const [isListingFormOpen, setIsListingFormOpen] = useState(false);

  const [mapProperties, setMapProperties] = useState([]);
  const [loadingMapProperties, setLoadingMapProperties] = useState(false);

  const setViewMode = (mode) => {
    sessionStorage.setItem("propertyViewMode", mode);
    setViewModeState(mode);
  };

  useEffect(() => {
    if (!isAdmin && viewMode === "map") {
      setViewMode("grid");
    }
  }, [isAdmin, viewMode]);

  const [savedPropertyIds, setSavedPropertyIds] = useState(() => {
    try {
      const cachedSaved = sessionStorage.getItem("savedPropertyIds");
      if (cachedSaved) {
        const { ids, timestamp } = JSON.parse(cachedSaved);
        const cacheAge = Date.now() - timestamp;
        const CACHE_DURATION = 5 * 60 * 1000;
        if (cacheAge < CACHE_DURATION && ids) {
          return new Set(ids);
        }
      }
    } catch (e) {}
    return new Set();
  });

  const [pagination, setPagination] = useState(() => {
    try {
      const cachedData = sessionStorage.getItem("propertiesCache");
      if (cachedData) {
        const {
          pagination: cachedPagination,
          timestamp,
          data,
        } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        const CACHE_DURATION = 5 * 60 * 1000;
        if (
          cacheAge < CACHE_DURATION &&
          cachedPagination &&
          data &&
          data.length > 0
        ) {
          return cachedPagination;
        }
      }
    } catch (e) {}
    return {
      page: 1,
      limit: 10,
      total: 0,
      hasMore: false,
    };
  });

  const defaultFilters = {
    priceRange: [MIN_PRICE, MAX_PRICE],
    propertyType: "",
    propertySubType: "",
    district: "",
    taluk: "",
    bedrooms: "",
    bathrooms: "",
    amenities: [],
    sortBy: "newest",
  };

  const [filters, setFiltersState] = useState(() => {
    try {
      const savedFilters = sessionStorage.getItem("propertyFilters");
      if (savedFilters) {
        const parsedFilters = JSON.parse(savedFilters);
        return {
          ...defaultFilters,
          ...parsedFilters,
          priceRange: normalizePriceRange(parsedFilters?.priceRange),
        };
      }
    } catch (e) {}
    return defaultFilters;
  });

  const setFilters = (newFilters) => {
    const updatedFilters =
      typeof newFilters === "function" ? newFilters(filters) : newFilters;
    const normalizedFilters = {
      ...updatedFilters,
      priceRange: normalizePriceRange(updatedFilters?.priceRange),
    };
    sessionStorage.setItem("propertyFilters", JSON.stringify(normalizedFilters));
    setFiltersState(normalizedFilters);
  };

  useEffect(() => {
    const fetchSavedPropertyIds = async () => {
      if (isLoggedIn) {
        try {
          const cachedSaved = sessionStorage.getItem("savedPropertyIds");
          if (cachedSaved) {
            const { ids, timestamp } = JSON.parse(cachedSaved);
            const cacheAge = Date.now() - timestamp;
            const CACHE_DURATION = 5 * 60 * 1000;
            if (cacheAge < CACHE_DURATION && ids) {
              setSavedPropertyIds(new Set(ids));
              return;
            }
          }

          const response = await savedPropertyAPI.getSavedPropertyIds();
          const ids = response.data.data.propertyIds || [];
          setSavedPropertyIds(new Set(ids));

          sessionStorage.setItem(
            "savedPropertyIds",
            JSON.stringify({
              ids,
              timestamp: Date.now(),
            })
          );
        } catch (error) {
          console.error("Error fetching saved property IDs:", error);
        }
      } else {
        setSavedPropertyIds(new Set());
        sessionStorage.removeItem("savedPropertyIds");
      }
    };

    fetchSavedPropertyIds();
  }, [isLoggedIn]);

  const isFirstFetch = useRef(true);
  const latestFetchTokenRef = useRef(0);

  const getCacheKey = (overrides = {}) => {
    const effectiveIsSearchMode =
      typeof overrides.isSearchMode === "boolean"
        ? overrides.isSearchMode
        : isSearchMode;
    const effectiveSearchQuery =
      overrides.searchQuery !== undefined ? overrides.searchQuery : searchQuery;

    return JSON.stringify({
      isSearchMode: effectiveIsSearchMode,
      searchQuery: effectiveSearchQuery,
      filters,
    });
  };

  const matchesSearchQuery = (property, query) => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    if (!normalizedQuery) return true;

    const searchableValues = [
      property.property_id,
      property.propertyId,
      property.property_name,
      property.property_code,
      property.propertyCode,
      property.search_keywords,
      property.title,
      property.property_type,
      property.property_sub_type,
      property.propertyType,
      property.propertySubType,
      property.property_caption,
      property.property_description,
      property.description,
      property.otherDetails,
      property.anyOtherDetails,
      property.landmark,
      property.address_line1,
      property.address_line2,
      property.address_description,
      property.address,
      property.location,
      property.city,
      property.area,
      property.district,
      property.taluk,
      property.state,
      property.owner_name,
      property.broker_name,
    ]
      .filter((value) => value !== undefined && value !== null)
      .map((value) => String(value).toLowerCase());

    const searchableBlob = searchableValues.join(" ");

    // Exact phrase match still works for direct matches.
    if (searchableBlob.includes(normalizedQuery)) {
      return true;
    }

    // Combined search: every meaningful token should match somewhere in the property fields.
    const stopWords = new Set(["properties", "property", "in", "at", "on", "the", "a", "an", "for", "of", "near", "nearby", "space", "side", "front", "back"]);
    const rawTokens = normalizedQuery.split(/\s+/).filter(Boolean);
    const tokens = rawTokens.filter((token) => !stopWords.has(token));

    if (tokens.length === 0) {
      return searchableBlob.includes(normalizedQuery);
    }

    const tokenAliases = {
      bengalore: ["bangalore", "bengaluru"],
      bangalore: ["bengaluru"],
      bengaluru: ["bangalore"],
    };

    return tokens.every((token) => {
      if (searchableBlob.includes(token)) return true;

      const aliases = tokenAliases[token] || [];
      return aliases.some((alias) => searchableBlob.includes(alias));
    });
  };

  const fetchProperties = async (forceRefresh = false, overrides = {}) => {
    const fetchToken = ++latestFetchTokenRef.current;

    const effectiveIsSearchMode =
      typeof overrides.isSearchMode === "boolean"
        ? overrides.isSearchMode
        : isSearchMode;
    const effectiveSearchQuery =
      overrides.searchQuery !== undefined ? overrides.searchQuery : searchQuery;

    const cacheKey = getCacheKey({
      isSearchMode: effectiveIsSearchMode,
      searchQuery: effectiveSearchQuery,
    });
    const cachedData = sessionStorage.getItem("propertiesCache");

    if (!forceRefresh && cachedData) {
      try {
        const {
          key,
          data,
          pagination: cachedPagination,
          timestamp,
        } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        const CACHE_DURATION = 5 * 60 * 1000;

        if (cacheAge < CACHE_DURATION && data.length > 0 && key === cacheKey) {
          if (fetchToken !== latestFetchTokenRef.current) return;
          setProperties(data);
          setPagination(cachedPagination);
          setLoading(false);
          isFirstFetch.current = false;
          return;
        }

        if (isFirstFetch.current && key === cacheKey) {
          if (fetchToken !== latestFetchTokenRef.current) return;
          setProperties(data);
          setPagination(cachedPagination);
          setLoading(false);
          isFirstFetch.current = false;
          return;
        }
      } catch (e) {}
    }

    isFirstFetch.current = false;

    try {
      setLoading(true);

      const pageLimit = 12;

      setPagination((prev) => ({ ...prev, page: 1, limit: pageLimit }));

      let response;

      if (effectiveIsSearchMode && effectiveSearchQuery) {
        const searchFilters = {
          page: 1,
          limit: pageLimit,
        };

        if (filters.propertyType && filters.propertyType !== "all") {
          searchFilters.propertyType = filters.propertyType;
        }

        if (filters.propertySubType && filters.propertySubType !== "all") {
          searchFilters.propertySubType = filters.propertySubType;
        }

        if (filters.district && filters.district !== "all") {
          searchFilters.district = filters.district;
        }

        if (filters.taluk && filters.taluk !== "all") {
          searchFilters.taluk = filters.taluk;
        }

        if (filters.priceRange[0] > MIN_PRICE) {
          searchFilters.minPrice = filters.priceRange[0];
        }

        if (filters.priceRange[1] < MAX_PRICE) {
          searchFilters.maxPrice = filters.priceRange[1];
        }

        if (filters.bedrooms && filters.bedrooms !== "any") {
          searchFilters.bedrooms = filters.bedrooms;
        }

        if (filters.bathrooms && filters.bathrooms !== "any") {
          searchFilters.bathrooms = filters.bathrooms;
        }

        if (filters.sortBy) {
          searchFilters.sortBy = filters.sortBy;
        }

        // Backend-driven search using searchKeyword param
        response = await propertyAPI.searchProperties(effectiveSearchQuery, searchFilters);
        if (fetchToken !== latestFetchTokenRef.current) return;
        const rawProperties = response.data.data.properties || [];
        const transformedProperties = transformProperties(rawProperties);
        setProperties(transformedProperties);

        const newPagination = {
          page: response.data.page,
          limit: pageLimit,
          total: response.data.total,
          hasMore: response.data.hasMore,
        };
        setPagination(newPagination);

        sessionStorage.setItem(
          "propertiesCache",
          JSON.stringify({
            key: cacheKey,
            data: transformedProperties,
            pagination: newPagination,
            timestamp: Date.now(),
          })
        );
      } else {
        const apiFilters = {
          page: 1,
          limit: pageLimit,
        };

        if (filters.propertyType && filters.propertyType !== "all") {
          apiFilters.propertyType = filters.propertyType;
        }

        if (filters.propertySubType && filters.propertySubType !== "all") {
          apiFilters.propertySubType = filters.propertySubType;
        }

        if (filters.district && filters.district !== "all") {
          apiFilters.district = filters.district;
        }

        if (filters.taluk && filters.taluk !== "all") {
          apiFilters.taluk = filters.taluk;
        }

        if (filters.priceRange[0] > MIN_PRICE) {
          apiFilters.minPrice = filters.priceRange[0];
        }

        if (filters.priceRange[1] < MAX_PRICE) {
          apiFilters.maxPrice = filters.priceRange[1];
        }

        if (filters.bedrooms && filters.bedrooms !== "any") {
          apiFilters.bedrooms = filters.bedrooms;
        }

        if (filters.bathrooms && filters.bathrooms !== "any") {
          apiFilters.bathrooms = filters.bathrooms;
        }

        if (filters.sortBy) {
          apiFilters.sortBy = filters.sortBy;
        }

        response = await propertyAPI.getAllProperties(apiFilters);
        if (fetchToken !== latestFetchTokenRef.current) return;
        const rawProperties = response.data.data.properties || [];
        const transformedProperties = transformProperties(rawProperties);
        setProperties(transformedProperties);

        const newPagination = {
          page: response.data.page,
          limit: pageLimit,
          total: response.data.total,
          hasMore: response.data.hasMore,
        };
        setPagination(newPagination);

        sessionStorage.setItem(
          "propertiesCache",
          JSON.stringify({
            key: cacheKey,
            data: transformedProperties,
            pagination: newPagination,
            timestamp: Date.now(),
          })
        );
      }
    } catch (error) {
      if (fetchToken !== latestFetchTokenRef.current) return;
      console.error("Error fetching properties:", error);
      setProperties([]);
    } finally {
      if (fetchToken !== latestFetchTokenRef.current) return;
      setLoading(false);
    }
  };

  // Refetch when filters or search change (can use cache)
  useEffect(() => {
    if (!isFirstFetch.current) {
      const urlQuery = getQueryParamValue(location.search, "q");
      const stateQuery = getStateSearchQuery();
      const effectiveQuery = stateQuery || urlQuery;
      fetchProperties(false, {
        isSearchMode: Boolean(effectiveQuery) || isSearchMode,
        searchQuery: effectiveQuery || searchQuery,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.propertyType, filters.propertySubType, filters.district, filters.taluk, filters.priceRange[0], filters.priceRange[1], filters.bedrooms, filters.bathrooms, filters.sortBy, isSearchMode, searchQuery, location.search, location.state]);

  // Refetch properties when page becomes visible (e.g., after navigating back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if cache was invalidated while away
        const cachedData = sessionStorage.getItem("propertiesCache");
        if (!cachedData) {
          fetchProperties(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchAllPropertiesForMap = async () => {
    const cacheKey = `mapProperties_${JSON.stringify(filters)}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      try {
        const { data, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        const CACHE_DURATION = 5 * 60 * 1000;
        if (cacheAge < CACHE_DURATION && data && data.length > 0) {
          setMapProperties(data);
          return;
        }
      } catch (e) {}
    }

    try {
      setLoadingMapProperties(true);

      const apiFilters = {
        page: 1,
        limit: 1000,
      };

      if (filters.propertyType && filters.propertyType !== "all") {
        apiFilters.propertyType = filters.propertyType;
      }
      if (filters.propertySubType && filters.propertySubType !== "all") {
        apiFilters.propertySubType = filters.propertySubType;
      }
      if (filters.district && filters.district !== "all") {
        apiFilters.district = filters.district;
      }
      if (filters.taluk && filters.taluk !== "all") {
        apiFilters.taluk = filters.taluk;
      }
      if (filters.priceRange[0] > MIN_PRICE) {
        apiFilters.minPrice = filters.priceRange[0];
      }
      if (filters.priceRange[1] < MAX_PRICE) {
        apiFilters.maxPrice = filters.priceRange[1];
      }

      const response = await propertyAPI.getAllProperties(apiFilters);
      const rawProperties = response.data.data.properties || [];
      const transformedProperties = transformProperties(rawProperties);

      setMapProperties(transformedProperties);

      sessionStorage.setItem(cacheKey, JSON.stringify({
        data: transformedProperties,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error("Error fetching map properties:", error);
      setMapProperties([]);
    } finally {
      setLoadingMapProperties(false);
    }
  };

  useEffect(() => {
    if (viewMode === "map") {
      fetchAllPropertiesForMap();
    }
  }, [viewMode, filters]);

  const mapFilteredProperties = useMemo(() => {
    if (!isSearchMode || !searchQuery) {
      return mapProperties;
    }

    return mapProperties.filter((property) =>
      matchesSearchQuery(property, searchQuery)
    );
  }, [mapProperties, isSearchMode, searchQuery]);

  // List/grid results are already filtered by backend search API.
  // Avoid client-side re-filtering to prevent valid API hits from disappearing.
  const displayedProperties = useMemo(() => properties, [properties]);

  const visibleProperties = viewMode === "map" ? mapFilteredProperties : displayedProperties;
  const visibleCount = visibleProperties.length;
  const totalVisibleResults = viewMode === "map" ? visibleCount : pagination.total;

  const handleLoadMore = async () => {
    if (loadingMore || !pagination.hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = pagination.page + 1;

      const apiFilters = {
        page: nextPage,
        limit: 12,
      };

      if (filters.propertyType && filters.propertyType !== "all") {
        apiFilters.propertyType = filters.propertyType;
      }

      if (filters.propertySubType && filters.propertySubType !== "all") {
        apiFilters.propertySubType = filters.propertySubType;
      }

      if (filters.district && filters.district !== "all") {
        apiFilters.district = filters.district;
      }

      if (filters.taluk && filters.taluk !== "all") {
        apiFilters.taluk = filters.taluk;
      }

      if (filters.priceRange[0] > MIN_PRICE) {
        apiFilters.minPrice = filters.priceRange[0];
      }

      if (filters.priceRange[1] < MAX_PRICE) {
        apiFilters.maxPrice = filters.priceRange[1];
      }

      if (filters.bedrooms && filters.bedrooms !== "any") {
        apiFilters.bedrooms = filters.bedrooms;
      }

      if (filters.bathrooms && filters.bathrooms !== "any") {
        apiFilters.bathrooms = filters.bathrooms;
      }

      if (filters.sortBy) {
        apiFilters.sortBy = filters.sortBy;
      }

      const response = isSearchMode && searchQuery
        ? await propertyAPI.searchProperties(searchQuery, apiFilters)
        : await propertyAPI.getAllProperties(apiFilters);
      const rawProperties = response.data.data.properties || [];
      const transformedProperties = transformProperties(rawProperties);

      setProperties((prev) => [...prev, ...transformedProperties]);
      setPagination({
        page: response.data.page,
        limit: 10,
        total: response.data.total,
        hasMore: response.data.hasMore,
      });
    } catch (error) {
      console.error("Error loading more properties:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleViewDetails = (property) => {
    const actualPropertyId =
      property.property_id || property.propertyId || property.propertyCode || property.id;
    const encryptedPropertyId = encryptRouteParam(actualPropertyId);

    window.open(`/property/${encryptedPropertyId}`, '_blank', 'noopener,noreferrer');
  };

  const handleListPropertyClick = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }
    setIsListingFormOpen(true);
  };

  const handleCloseListingForm = () => {
    setIsListingFormOpen(false);
  };

  const handlePropertyListingSuccess = () => {
    sessionStorage.removeItem("propertiesCache");
    fetchProperties(true);
  };

  const handleSignIn = () => {
    navigate("/login");
  };

  const handleSignOut = () => {
    logout();
    navigate("/");
  };

  const handleSearch = (query) => {
    const currentState = location.state || {};
    const { seoSearchQuery: _removedSeoSearchQuery, ...stateWithoutSeoQuery } = currentState;
    const clearedState = Object.keys(stateWithoutSeoQuery).length
      ? stateWithoutSeoQuery
      : undefined;

    if (!query || query.trim() === "") {
      setSearchQuery("");
      setIsSearchMode(false);
      navigate("/", {
        replace: true,
        state: clearedState,
      });
      return;
    }

    const normalizedQuery = query.trim();
    setSearchQuery(normalizedQuery);
    setIsSearchMode(true);

    if (location.pathname !== "/") {
      navigate("/", {
        replace: false,
        state: {
          ...stateWithoutSeoQuery,
          seoSearchQuery: normalizedQuery,
        },
      });
    } else {
      navigate("/", {
        replace: true,
        state: {
          ...stateWithoutSeoQuery,
          seoSearchQuery: normalizedQuery,
        },
      });
    }

    setTimeout(() => {
      const propertySection = document.getElementById("main-content");
      if (propertySection) {
        propertySection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const handleMyPropertiesClick = () => {
    navigate("/my-properties");
  };

  const handleManageMyPropertiesClick = () => {
    navigate("/property-list");
  };

  const handleSavedPropertiesClick = () => {
    navigate("/saved-properties");
  };

  const handleMyProfileClick = () => {
    navigate("/my-profile");
  };

  const handleHomeClick = () => {
    sessionStorage.removeItem("propertiesCache");
    navigate("/", {
      replace: location.pathname === "/",
      state: {
        ...(location.state || {}),
        homeRefreshAt: Date.now(),
      },
    });
  };

  const handleSaveToggle = (propertyId, isSaved) => {
    setSavedPropertyIds((prev) => {
      const newSet = new Set(prev);
      if (isSaved) {
        newSet.add(propertyId);
      } else {
        newSet.delete(propertyId);
      }

      sessionStorage.setItem(
        "savedPropertyIds",
        JSON.stringify({
          ids: Array.from(newSet),
          timestamp: Date.now(),
        })
      );

      return newSet;
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {isLoggedIn && isDesktopView && (
        <Sidebar
          isOpen={isOpen}
          onToggle={toggleDrawer}
          screenWidth={isDesktopView}
          Drawer={Drawer}
        />
      )}
      <Box 
        component="main"
        sx={{ 
          display: "flex", 
          flexDirection: "column",
          minHeight: "100vh",
          marginLeft: isLoggedIn && isDesktopView
            ? (isOpen
              ? { md: '14.5%', lg: '14.5%', xl: '11%' }
              : { md: '5.5%', lg: '3.5%' })
            : 0,
          transition: 'margin-left 0.225s cubic-bezier(0, 0, 0.2, 1)',
        }}
      >
        <Header
          onListPropertyClick={handleListPropertyClick}
          isLoggedIn={isLoggedIn}
          isAdmin={isAdmin}
          userEmail={userProfile?.email || ""}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          onSearch={handleSearch}
          onMyPropertiesClick={handleMyPropertiesClick}
          onManageMyPropertiesClick={handleManageMyPropertiesClick}
          onUnderVerificationClick={() => navigate('/all-properties')}
          onUsersClick={() => navigate('/users')}
          onSavedPropertiesClick={handleSavedPropertiesClick}
          onMyProfileClick={handleMyProfileClick}
          onHomeClick={handleHomeClick}
        />
        <HeroSection
        filters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onSearch={handleSearch}
      />
      <MouseScrollIndicator targetId="main-content" />

      <Container
        component="main"
        maxWidth="xl"
        id="main-content"
        sx={{ py: { xs: 2, md: 3 } }}
      >
        <ScrollReveal>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { md: "center" },
              justifyContent: "space-between",
              mb: 3,
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                component="h2"
                sx={{ fontWeight: 600, mb: 1 }}
              >
                {isSearchMode
                  ? `Search Results for "${searchQuery}"`
                  : "Properties for Sale in Karnataka"}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Showing {visibleCount} of {totalVisibleResults}{" "}
                {isSearchMode ? "result(s)" : "properties"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy}
                  label="Sort By"
                  onChange={(e) =>
                    setFilters({ ...filters, sortBy: e.target.value })
                  }
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="price-low">Price: Low to High</MenuItem>
                  <MenuItem value="price-high">Price: High to Low</MenuItem>
                  <MenuItem value="area-large">Largest Area</MenuItem>
                  <MenuItem value="area-small">Smallest Area</MenuItem>
                </Select>
              </FormControl>
              <ViewModeSelector
                currentView={viewMode}
                onViewChange={setViewMode}
                showMap={isAdmin}
              />
            </Box>
          </Box>
        </ScrollReveal>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={60} />
          </Box>
        ) : visibleCount === 0 ? (
          <ScrollReveal>
            <Box sx={{ textAlign: "center", py: 5 }}>
              <Box sx={{ color: "text.secondary", mb: 2 }}>
                <svg
                  style={{
                    width: "64px",
                    height: "64px",
                    margin: "0 auto 16px",
                  }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                No properties found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Try adjusting your filters to see more results
              </Typography>
            </Box>
          </ScrollReveal>
        ) : (
          <>
            {viewMode === "grid" && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                    xl: "repeat(4, 1fr)",
                    xxl: "repeat(4, 1fr)",
                  },
                  gap: 3,
                  maxWidth: "100%",
                  overflow: "hidden",
                  "& > *": {
                    minHeight: 0,
                    minWidth: 0,
                  },
                }}
              >
                {displayedProperties.map((property, index) => (
                  <PropertyCard
                    key={`${property.id}-${index}`}
                    property={property}
                    onViewDetails={handleViewDetails}
                    isLoggedIn={isLoggedIn}
                    user={userProfile}
                    initialSavedState={savedPropertyIds.has(property.id)}
                    onSaveToggle={handleSaveToggle}
                    imagePriority={index < 4}
                  />
                ))}
              </Box>
            )}
            {viewMode === "list" && (
              <PropertyListView
                properties={displayedProperties}
                onViewDetails={handleViewDetails}
                isLoggedIn={isLoggedIn}
                user={userProfile}
                savedPropertyIds={Array.from(savedPropertyIds)}
                onSaveToggle={handleSaveToggle}
                imagePriorityCount={3}
              />
            )}
            {viewMode === "map" && (
              <Suspense
                fallback={
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 8 }}
                  >
                    <CircularProgress size={60} />
                  </Box>
                }
              >
                {loadingMapProperties ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 8,
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={60} />
                    <Typography variant="body1" color="text.secondary">
                      Loading all properties for map...
                    </Typography>
                  </Box>
                ) : (
                  <PropertyMapView
                    properties={visibleProperties}
                    onViewDetails={handleViewDetails}
                    isLoggedIn={isLoggedIn}
                    user={user}
                  />
                )}
              </Suspense>
            )}

            {pagination.hasMore && viewMode !== "map" && (
              <Box
                sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  sx={{
                    px: 6,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "1rem",
                  }}
                >
                  {loadingMore ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ mr: 1, color: "white" }}
                      />
                      Loading...
                    </>
                  ) : (
                    "View More Properties"
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* <Testimonials />

        <Footer /> */}

        <Suspense fallback={null}>
          <PropertyListingForm
            isOpen={isListingFormOpen}
            onClose={handleCloseListingForm}
            onSuccess={handlePropertyListingSuccess}
          />
        </Suspense>
      </Box>
    </Box>
  );
}
